// Sound settings: master mute, volume and which events sound. Per viewer, in
// localStorage, until account settings sync (operator, 2026-11-04: on by
// default only for your own trade results and alerts; round 2, 2026-11-05:
// a buy and a sell each have their own sound, and the events that can be noise
// start off).

export type SoundEvent =
  | 'buy'
  | 'sell'
  | 'tradeFailure'
  | 'limitFill'
  | 'alert'
  | 'tracked'
  | 'withdrawal'
  | 'click'

export const SOUND_EVENTS: readonly SoundEvent[] = [
  'buy',
  'sell',
  'tradeFailure',
  'limitFill',
  'alert',
  'tracked',
  'withdrawal',
  'click',
]

export const SOUND_EVENT_LABEL: Record<SoundEvent, string> = {
  buy: 'Buy filled',
  sell: 'Sell filled',
  tradeFailure: 'Trade failed',
  limitFill: 'Limit order filled',
  alert: 'Price alert',
  tracked: 'Tracked wallet trade',
  withdrawal: 'Transfer sent',
  click: 'Buy / Sell press',
}

export interface SoundSettings {
  muted: boolean
  /** 0..1 */
  volume: number
  enabled: Record<SoundEvent, boolean>
}

export const DEFAULT_SOUND_SETTINGS: SoundSettings = {
  muted: false,
  volume: 0.6,
  enabled: {
    buy: true,
    sell: true,
    tradeFailure: true,
    limitFill: true,
    alert: true,
    tracked: false,
    withdrawal: true,
    click: false,
  },
}

const KEY = 'wt-sound'

export function parseSoundSettings(raw: string | null): SoundSettings {
  if (!raw) return DEFAULT_SOUND_SETTINGS
  try {
    const data = JSON.parse(raw) as Partial<SoundSettings>
    const volume =
      typeof data.volume === 'number' && Number.isFinite(data.volume)
        ? Math.min(1, Math.max(0, data.volume))
        : DEFAULT_SOUND_SETTINGS.volume
    const enabled = { ...DEFAULT_SOUND_SETTINGS.enabled }
    const stored = (data.enabled ?? {}) as Record<string, unknown>
    // Round 1 had one 'tradeSuccess' switch: it now stands for both sides.
    if (typeof stored.tradeSuccess === 'boolean') {
      enabled.buy = stored.tradeSuccess
      enabled.sell = stored.tradeSuccess
    }
    for (const event of SOUND_EVENTS) {
      const value = stored[event]
      if (typeof value === 'boolean') enabled[event] = value
    }
    return { muted: data.muted === true, volume, enabled }
  } catch {
    return DEFAULT_SOUND_SETTINGS
  }
}

let current: SoundSettings | null = null
const listeners = new Set<() => void>()

export function readSoundSettings(): SoundSettings {
  if (current) return current
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(KEY)
  } catch {
    raw = null
  }
  current = parseSoundSettings(raw)
  return current
}

export function writeSoundSettings(next: SoundSettings): void {
  current = next
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    // private window / blocked storage: the setting lives for this visit
  }
  for (const listener of listeners) listener()
}

export function subscribeSoundSettings(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function resetSoundSettingsForTest(): void {
  current = null
  listeners.clear()
}
